import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  addFax,
  addFaxFolder,
  bulkDeleteFax,
  bulkUpdateFax,
  deleteFax,
  deleteFaxFolder,
  fetchAllFax,
  fetchAllFaxFolders,
  fetchFaxCounts,
  fetchFaxSettings,
  updateFax,
  updateFaxFolder,
  fetchAllFaxTags,
  addFaxTag,
  updateFaxTag,
  deleteFaxTag,
  addCoversheet,
  fetchAllCoversheets,
} from '@store/actions'
import {
  IFaxComposeSeed,
  IFaxCoversheet,
  IFaxFolder,
  IFaxMessage,
  IFaxTag,
} from '@dataTypes/fax.type'
import { IFaxSettings } from '@services/api/fax/settings.api'
import { partitionFaxMessages, mergeFaxViewData } from '@utils/fax.util'

type FaxState = {
  inbox: IFaxMessage[]
  sent: IFaxMessage[]
  archive: IFaxMessage[]
  draft: IFaxMessage[]
  trash: IFaxMessage[]
  history: IFaxMessage[]
  data: IFaxMessage[]
  blocked: IFaxMessage[]
  counts: Record<string, number>
  settings: IFaxSettings | null
  tags: IFaxTag[]
  folders: IFaxFolder[]
  coversheets: IFaxCoversheet[]
  selectedFaxId: string | null
  activeFax: IFaxMessage | null
  activeFolder: string
  searchQuery: string
  faxFilters: Record<string, string[]>
  composeSeed: IFaxComposeSeed | null
  error: string | null
  loading: boolean
}

const initialState: FaxState = {
  inbox: [],
  sent: [],
  archive: [],
  draft: [],
  trash: [],
  history: [],
  data: [],
  blocked: [],
  counts: {},
  settings: null,
  tags: [],
  folders: [],
  coversheets: [],
  selectedFaxId: null,
  activeFax: null,
  activeFolder: 'inbox',
  searchQuery: '',
  faxFilters: {},
  composeSeed: null,
  loading: false,
  error: null,
}

const faxSlice = createSlice({
  name: 'fax',
  initialState,
  reducers: {
    setActiveFolder: (state, action: PayloadAction<string>) => {
      if (state.activeFolder !== action.payload) {
        state.faxFilters = {}
        state.searchQuery = ''
      }
      state.activeFolder = action.payload
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    setFaxFilters: (state, action: PayloadAction<Record<string, string[]>>) => {
      state.faxFilters = action.payload
    },
    setActiveFax: (state, action: PayloadAction<IFaxMessage | null>) => {
      state.activeFax = action.payload
    },
    setSelectedFaxId: (state, action: PayloadAction<string | null>) => {
      state.selectedFaxId = action.payload
    },
    setFaxComposeSeed: (
      state,
      action: PayloadAction<IFaxComposeSeed | null>,
    ) => {
      state.composeSeed = action.payload
    },
    clearFaxComposeSeed: (state) => {
      state.composeSeed = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllFax.fulfilled, (state, action) => {
        const incoming = action.payload
        const view = action.meta.arg as string | undefined

        state.data = view
          ? mergeFaxViewData(state.data, incoming, view)
          : incoming

        const { inbox, sent, archive, draft, trash, history } =
          partitionFaxMessages(state.data)
        state.inbox = inbox
        state.sent = sent
        state.archive = archive
        state.draft = draft
        state.trash = trash
        state.history = history
        state.loading = false
        state.error = null
      })
      .addCase(fetchAllFax.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllFax.rejected, (state) => {
        state.error = 'Failed to fetch fax data'
        state.loading = false
      })

    builder.addCase(
      fetchFaxCounts.fulfilled,
      (state, action: PayloadAction<Record<string, number>>) => {
        state.counts = action.payload
      },
    )

    builder.addCase(
      fetchFaxSettings.fulfilled,
      (state, action: PayloadAction<IFaxSettings | null>) => {
        state.settings = action.payload
      },
    )

    builder
      .addCase(
        updateFax.fulfilled,
        (state, action: PayloadAction<IFaxMessage>) => {
          state.data = state.data.map((f) =>
            f.fax_message_id === action.payload.fax_message_id
              ? action.payload
              : f,
          )
          const { inbox, sent, archive, draft, trash, history } =
            partitionFaxMessages(state.data)
          state.inbox = inbox
          state.sent = sent
          state.archive = archive
          state.draft = draft
          state.trash = trash
          state.history = history
          state.loading = false
          state.error = null
        },
      )
      .addCase(updateFax.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateFax.rejected, (state) => {
        state.error = 'Failed to fetch fax data'
        state.loading = false
      })

    builder
      .addCase(
        bulkUpdateFax.fulfilled,
        (state, action: PayloadAction<IFaxMessage[]>) => {
          state.data = state.data.map((f) => {
            const updated = action.payload.find(
              (u) => u.fax_message_id === f.fax_message_id,
            )
            let _tag: IFaxTag | undefined = undefined
            if (updated && updated.fax_tag_id) {
              _tag = state.tags.find((t) => t.fax_tag_id === updated.fax_tag_id)
            }

            return {
              ...(updated ?? f),
              tags: _tag ?? f.fax_tag,
            }
          })
          const { inbox, sent, archive, draft, trash, history } =
            partitionFaxMessages(state.data)
          state.inbox = inbox
          state.sent = sent
          state.archive = archive
          state.draft = draft
          state.trash = trash
          state.history = history
          state.loading = false
          state.error = null
        },
      )
      .addCase(bulkUpdateFax.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(bulkUpdateFax.rejected, (state) => {
        state.error = 'Failed to bulk update fax'
        state.loading = false
      })

    builder
      .addCase(
        addFax.fulfilled,
        (state, action: PayloadAction<IFaxMessage | void>) => {
          if (action.payload?.fax_message_id) {
            const exists = state.data.some(
              (f) => f.fax_message_id === action.payload!.fax_message_id,
            )
            if (!exists) {
              state.data = [action.payload as IFaxMessage, ...state.data]
            }
            const partitioned = partitionFaxMessages(state.data)
            state.inbox = partitioned.inbox
            state.sent = partitioned.sent
            state.archive = partitioned.archive
            state.draft = partitioned.draft
            state.trash = partitioned.trash
            state.history = partitioned.history
          }
          state.loading = false
          state.error = null
        },
      )
      .addCase(addFax.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addFax.rejected, (state) => {
        state.error = 'Failed to fetch fax data'
        state.loading = false
      })

    builder
      .addCase(
        bulkDeleteFax.fulfilled,
        (state, action: PayloadAction<string[]>) => {
          const deletedIds = new Set(action.payload)
          state.data = state.data.filter(
            (fax) => fax.fax_message_id && !deletedIds.has(fax.fax_message_id),
          )
          const { inbox, sent, archive, draft, trash, history } =
            partitionFaxMessages(state.data)
          state.inbox = inbox
          state.sent = sent
          state.archive = archive
          state.draft = draft
          state.trash = trash
          state.history = history
          state.loading = false
          state.error = null
        },
      )
      .addCase(bulkDeleteFax.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(bulkDeleteFax.rejected, (state) => {
        state.error = 'Failed to bulk delete fax'
        state.loading = false
      })

    builder
      .addCase(deleteFax.fulfilled, (state, action: PayloadAction<string>) => {
        state.data = state.data.filter(
          (fax) => fax.fax_message_id !== action.payload,
        )
        const { inbox, sent, archive, draft, trash, history } =
          partitionFaxMessages(state.data)
        state.inbox = inbox
        state.sent = sent
        state.archive = archive
        state.draft = draft
        state.trash = trash
        state.history = history
        state.loading = false
        state.error = null
      })
      .addCase(deleteFax.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteFax.rejected, (state) => {
        state.error = 'Failed to fetch fax data'
        state.loading = false
      })

    builder
      .addCase(
        fetchAllFaxTags.fulfilled,
        (state, action: PayloadAction<IFaxTag[]>) => {
          state.tags = action.payload
          state.error = null
        },
      )
      .addCase(fetchAllFaxTags.rejected, (state) => {
        state.error = 'Failed to fetch fax tags'
      })

    builder
      .addCase(addFaxTag.fulfilled, (state, action: PayloadAction<IFaxTag>) => {
        const exists = state.tags.some(
          (t) => t.fax_tag_id === action.payload.fax_tag_id,
        )
        if (!exists) {
          state.tags.push(action.payload)
        }
        state.error = null
      })
      .addCase(addFaxTag.rejected, (state) => {
        state.error = 'Failed to add fax tag'
      })

    builder
      .addCase(
        updateFaxTag.fulfilled,
        (state, action: PayloadAction<IFaxTag>) => {
          const idx = state.tags.findIndex(
            (t) => t.fax_tag_id === action.payload.fax_tag_id,
          )
          if (idx !== -1) {
            state.tags[idx] = action.payload
          }
          state.error = null
        },
      )
      .addCase(updateFaxTag.rejected, (state) => {
        state.error = 'Failed to update fax tag'
      })

    builder
      .addCase(
        deleteFaxTag.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.tags = state.tags.filter((t) => t.fax_tag_id !== action.payload)
          state.error = null
        },
      )
      .addCase(deleteFaxTag.rejected, (state) => {
        state.error = 'Failed to delete fax tag'
      })

    builder
      .addCase(
        fetchAllFaxFolders.fulfilled,
        (state, action: PayloadAction<IFaxFolder[]>) => {
          state.folders = action.payload
          state.error = null
        },
      )
      .addCase(fetchAllFaxFolders.rejected, (state) => {
        state.error = 'Failed to fetch fax folders'
      })

    builder
      .addCase(
        addFaxFolder.fulfilled,
        (state, action: PayloadAction<IFaxFolder>) => {
          const exists = state.folders.some(
            (f) => f.fax_folder_id === action.payload.fax_folder_id,
          )
          if (!exists) {
            state.folders.push(action.payload)
          }
          state.error = null
        },
      )
      .addCase(addFaxFolder.rejected, (state) => {
        state.error = 'Failed to add fax folder'
      })

    builder
      .addCase(
        updateFaxFolder.fulfilled,
        (state, action: PayloadAction<IFaxFolder>) => {
          const idx = state.folders.findIndex(
            (f) => f.fax_folder_id === action.payload.fax_folder_id,
          )
          if (idx !== -1) {
            state.folders[idx] = action.payload
          }
          state.error = null
        },
      )
      .addCase(updateFaxFolder.rejected, (state) => {
        state.error = 'Failed to update fax folder'
      })

    builder
      .addCase(
        deleteFaxFolder.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.folders = state.folders.filter(
            (f) => f.fax_folder_id !== action.payload,
          )
          state.error = null
        },
      )
      .addCase(deleteFaxFolder.rejected, (state) => {
        state.error = 'Failed to delete fax folder'
      })

    builder
      .addCase(
        fetchAllCoversheets.fulfilled,
        (state, action: PayloadAction<IFaxCoversheet[]>) => {
          state.coversheets = action.payload
        },
      )
      .addCase(
        addCoversheet.fulfilled,
        (state, action: PayloadAction<IFaxCoversheet>) => {
          const exists = state.coversheets.some(
            (c) => c.fax_coversheet_id === action.payload.fax_coversheet_id,
          )
          if (!exists) state.coversheets.push(action.payload)
        },
      )
  },
})

export const {
  setActiveFolder,
  setSearchQuery,
  setFaxFilters,
  setActiveFax,
  setSelectedFaxId,
  setFaxComposeSeed,
  clearFaxComposeSeed,
} = faxSlice.actions

export default faxSlice.reducer
